import * as React from 'react';

import './styles.scss';
import classNames from 'classnames';
import { Popover, PopoverInteractionKind } from '@blueprintjs/core';
import Menu from '../Menu';
import Icon from '../Icon';
import {PropsWithChildren} from "react";

export interface IMenuItemProps extends PropsWithChildren {
    /** Item text */
    text: React.ReactNode;
    /** Mark as active */
    active?: boolean;
    /** className to append */
    className?: string;
    /** disabled status */
    disabled?: boolean;
    /** Click event handler */
    onClick?: (e: React.SyntheticEvent<HTMLElement>) => void;
    /** Mouse Down event handler */
    onMouseDown?: (event) => void;
    /** Link URL */
    href?: string;
    /** Link target attribute. */
    target?: string;
    /** Reference to first level DOM node */
    innerRef?: (instance: HTMLLIElement | null) => void;
    /** React-router link component to use instead of HTML A tag. If defined href is passed as "to" prop. */
    link?: any;
    /** Notification count */
    notifications?: number;
    /** Intent to use */
    intent?: 'default' | 'danger' | 'new' | 'link';
    /** Does not require last element to be exact, useful when using urls with random parameters or IDs */
    exact?: boolean;
    /** When true, the trailing slash on a location’s pathname will be taken into consideration when determining if the location matches the current URL. */
    strict?: boolean;
    /** AddOn to render */
    addOn?: React.ReactNode;
    /** Test ID for the element */
    testId?: string;
    children?: React.ReactNode;
}

/**
 * Blueprint MenuItem replacement component due to not having ref.
 */
export const MenuItem: React.FunctionComponent<IMenuItemProps> = (props) => {
    let Link: any = 'a';
    if (props.link) {
        Link = props.link;
    }

    const RenderLink = () => {
        return (
            <Link
                className={classNames('menu-item bp5-menu-item bp5-popover-dismiss', {
                    'active': props.active,
                    'disabled': props.disabled,
                    'intent-default': props.intent === 'default' || !props.intent,
                    'intent-danger': props.intent === 'danger',
                    'intent-new': props.intent === 'new',
                    'intent-link': props.intent === 'link',
                }, props.className)}
                onClick={props.onClick}
                onMouseDown={props.onMouseDown}
                {...(props.link && props.active === false ? { isActive: () => false, } : {})}
                {...(props.link ? {
                    to: props.href,
                    exact: props.exact,
                    strict: props.strict,
                } : {
                    href: props.href,
                })}
                data-testid={props.testId}
            >
                <div className="bp5-text-overflow-ellipsis bp5-fill">{props.text}</div>
                {!!props.notifications && (
                    <div className="bp5-menu-item-notifications">{props.notifications}</div>
                )}
                {!!props.addOn && (
                    <div className="bp5-menu-item-label">{props.addOn}</div>
                )}
                {!!props.children && (
                    <Icon kind="arrow1-right" width={10} height={20} />
                )}
            </Link>
        );
    };

    return (
        <li ref={props.innerRef}>
            {!!props.children ? (
                <Popover
                    enforceFocus={false}
                    interactionKind={PopoverInteractionKind.HOVER}
                    position="right-top"
                    usePortal={true}
                    minimal={true}
                    autoFocus={false}
                    modifiers={{
                        offset: {
                            data: [-1, 0],
                        },
                        flip: {
                            enabled: true,
                        },
                    }}
                    // className="dropper"
                    portalClassName={'site-menu-portal'}
                    popoverClassName={'site-menu-popover'}
                    content={(
                        <Menu>
                            {props.children}
                        </Menu>
                    )}
                    transitionDuration={0}
                    hoverOpenDelay={0}
                    hoverCloseDelay={100}
                >
                    <RenderLink/>
                </Popover>
            ) : (
                <RenderLink/>
            )}
        </li>
    );
    /*
        <BlueprintMenuItem {...this.props} />
    */
};

MenuItem.displayName = 'MenuItem';

export default MenuItem;
